import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { VIEW } from "../../../../utils/breakpoint";
import { useEffect, useState } from "react";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { SELECTED_ZONE_QUERY_PARAM } from "../../../../utils/const";
import { COLOR } from "../../../../utils/colors";
import { shadow } from "../../../../utils/styles";
import { Close, Key, KeyboardArrowDown, Remove } from "@mui/icons-material";
import SweepIcon from "../../../../components/icons/SweepIcon";
import TrashIcon from "../../../../components/icons/TrashIcon";
import { useListAvailableZones } from "../../../../hooks/cleaning";
import { getProfile } from "../../../../utils/api";
import { useListMap } from "../../../../hooks/map";
import { useDispatch, useSelector } from "react-redux";
import { setIsExpandSelectedZone } from "../../../../store/cleaning/cleaningSlice";
import { useExpandSelectedZone, useExpandUnselectedZone } from "../../../../hooks/cleaning/state";

function getPolygonCentroid(coordinates: number[][][]) {
  const points = coordinates[0]; // outer ring
  let x = 0, y = 0;

  points.forEach(([lng, lat]) => {
    x += lng;
    y += lat;
  });

  return {
    lng: x / points.length,
    lat: y / points.length
  };
}

export function addRippleLayer(map: mapboxgl.Map) {
  if (map.getSource("ripple")) return;

  map.addSource("ripple", {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: []
    }
  });

  map.addLayer({
    id: "ripple-layer",
    type: "circle",
    source: "ripple",
    paint: {
      "circle-radius": ["get", "radius"],
      "circle-color": COLOR.accent.green,
      "circle-opacity": ["get", "opacity"],
      "circle-stroke-width": 2,
      "circle-stroke-color": COLOR.accent.green,
      "circle-stroke-opacity": ["get", "opacity"]
    },
    slot: 'middle'
  });
}

export function triggerRipple(
  map: mapboxgl.Map,
  lngLat: { lng: number; lat: number }
) {
  const start = performance.now();
  const duration = 900;

  const animate = (time: number) => {
    const t = Math.min((time - start) / duration, 1);

    const radius = 6 + t * 60;
    const opacity = 1 - t;

    const geojson = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [lngLat.lng, lngLat.lat]
          },
          properties: {
            radius,
            opacity
          }
        }
      ]
    };

    (map.getSource("ripple") as mapboxgl.GeoJSONSource).setData(geojson as GeoJSON.FeatureCollection);

    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      // cleanup
      (map.getSource("ripple") as mapboxgl.GeoJSONSource).setData({
        type: "FeatureCollection",
        features: []
      });
    }
  };

  requestAnimationFrame(animate);
}

export default function ZoneSelectedDialog() {

  const { isExpanded, setIsExpanded } = useExpandSelectedZone()
  const { isExpandedUnselected, setIsExpandedUnselected } = useExpandUnselectedZone()

  const [selectedZones, setSelectedZones] = useQueryState(SELECTED_ZONE_QUERY_PARAM, parseAsArrayOf(parseAsString, ';').withDefault([]))
  const organisation_id = getProfile()?.organisation
  const { data } = useListAvailableZones({
    organisation_id,
  });
  const currentFloor = useSelector((state: any) => state.map.currentFloor);
  // const zoneOptions = mockZoneOptions;
  const zoneOptions = data?.data || []
  const findZone = (zoneId: string) => {
    return zoneOptions.find((zone) => zone.uuid === zoneId);
  }

  const { data: dataListMaps } = useListMap({
    page_size: 100
  })

  const listMaps = dataListMaps?.data || []
  const currentMap = listMaps.find((map) => map.map_level.toString() === currentFloor.toString())
  const listCurrentAvailableZone = zoneOptions.filter((zone) => zone.map === currentMap?.map_uuid).filter((zone) => !selectedZones.includes(zone.uuid));

  const findMap = (map_uuid?: string) => {
    if (!map_uuid) {
      return undefined
    }
    return listMaps.find((data) => data.map_uuid === map_uuid)
  }

  return (
    <Stack gap={1} sx={(theme) => ({
      [theme.breakpoints.up(VIEW.PHONE)]: {
        right: "15px",
        left: "15px",
        top: "10px",
        // maxWidth: '330px',
        mx: 'auto'
      },
      [theme.breakpoints.up(VIEW.TABLET)]: {
        left: "auto",
        marginLeft: 'auto',
        right: "20px",
        top: "10px",
        // maxWidth: '340px',
      },
      [theme.breakpoints.up(VIEW.DESKTOP)]: {
        left: "auto",
        marginLeft: 'auto',
        top: "10px"
      },
      color: COLOR.black,
      position: 'absolute',
      transition: 'all 0.3s',
    })}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: COLOR.white,
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: isExpanded && selectedZones.length > 0 ? '16px' : '0px',
          minWidth: isExpanded ? '480px' : '280px',
          transition: 'all 0.3s',
          zIndex: 1200,
          ...shadow
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
            No of Selected Zones: <span style={{ fontWeight: 'bold', color: COLOR.accent.blue }}>{selectedZones.length}</span>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {!isExpanded && (
              <Box onClick={() => selectedZones.length > 0 && setIsExpanded(true)}>
                <KeyboardArrowDown sx={{ fontSize: '24px', ml: '14px', cursor: 'pointer', opacity: selectedZones.length > 0 ? 1 : 0, rotate: selectedZones.length === 0 ? '-90deg' : '0deg', transition: 'all 0.3s' }} />
              </Box>
            )}
            {isExpanded && (
              <Box onClick={() => setIsExpanded(false)}>
                <Close sx={{ fontSize: '32px', ml: '14px', cursor: 'pointer' }} />
              </Box>
            )}
          </Box>
        </Box>

        {/* {isExpanded && selectedZones.length > 0 && ( */}
        <Box sx={{ display: 'flex', transition: 'all 0.3s', flexDirection: 'column', padding: isExpanded && selectedZones.length > 0 ? '8px' : '0', borderRadius: '8px', gap: '16px', height: isExpanded && selectedZones.length > 0 ? "auto" : '0px', overflowY: isExpanded && selectedZones.length > 0 ? "auto" : "hidden", maxHeight: isExpandedUnselected ? '30vh' : '60vh' }}>
          {selectedZones.map((zone, index) => (
            <Box key={index} sx={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FAFAFA', borderRadius: '8px' }}>
              <SweepIcon />
              <Box sx={{ ml: '24px', display: 'flex', flex: 'auto', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                  {findZone(zone)?.area_name || 'Unknown Zone'}
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 400, color: COLOR.gray[700] }}>
                  {findMap(findZone(zone)?.map)?.map_name_display || 'Unknown Floor'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: COLOR.accent.danger,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const newSelectedZones = selectedZones.filter((z) => z !== zone);
                  setSelectedZones(newSelectedZones);
                }}
              >
                <TrashIcon />
              </Box>
            </Box>
          ))}
        </Box>
        {/* )} */}
      </Box>
      <Box
        key="unselected-zones"
        sx={(theme) => ({
          backgroundColor: COLOR.white,
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: isExpandedUnselected && listCurrentAvailableZone.length > 0 ? '8px' : '0px',
          minWidth: isExpandedUnselected ? '480px' : '280px',
          transition: 'all 0.3s',
          zIndex: 1200,
          ...shadow
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
            No of Unselected Zones: <span style={{ fontWeight: 'bold', color: COLOR.accent.blue }}>{listCurrentAvailableZone.length}</span>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {!isExpandedUnselected && (
              <Box
                onClick={() => {
                  if (listCurrentAvailableZone.length > 0) {
                    setIsExpandedUnselected(true)
                  }
                }}>
                <KeyboardArrowDown
                  sx={{
                    fontSize: '24px',
                    ml: '14px',
                    cursor: 'pointer',
                    opacity: listCurrentAvailableZone.length > 0 ? 1 : 0,
                    rotate: listCurrentAvailableZone.length === 0 ? '-90deg' : '0deg',
                    transition: 'all 0.3s'
                  }}
                />
              </Box>
            )}
            {isExpandedUnselected && (
              <Box onClick={() => {
                setIsExpandedUnselected(false)
              }}>
                <Close sx={{ fontSize: '32px', ml: '14px', cursor: 'pointer' }} />
              </Box>
            )}
          </Box>
        </Box>

        {/* {isExpanded && selectedZones.length > 0 && ( */}
        <Box
          sx={{
            display: 'flex',
            transition: 'all 0.3s',
            flexDirection: 'column',
            padding: isExpandedUnselected ? '16px' : '0',
            borderRadius: '8px', gap: '16px',
            height: isExpandedUnselected ? "auto" : '0px',
            overflowY: isExpandedUnselected ? "auto" : "hidden",
            maxHeight: isExpanded ? '25vh' : '60vh'
          }}>
          {listCurrentAvailableZone.map((zone, index) => (
            <Box
              key={zone.uuid}
              sx={{
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#FAFAFA',
                borderRadius: '8px'
              }}>
              <SweepIcon />
              <Box sx={{ ml: '24px', display: 'flex', flex: 'auto', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                  {findZone(zone.uuid)?.area_name || 'Unknown Zone'}
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 400, color: COLOR.gray[700] }}>
                  {findMap(findZone(zone.uuid)?.map)?.map_name_display || 'Unknown Floor'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  // transform: 'translateX(100px)',
                }}
              >
                <Checkbox onChange={(e) => {
                  e.preventDefault();
                  const isChecked = e.currentTarget.checked;
                  // if(isChecked) {
                  // e.currentTarget.style.marginLeft = '-100px';
                  // }
                  const parentNode = e.currentTarget.parentNode?.parentNode?.parentNode as HTMLElement;
                  if (!parentNode) {
                    return;
                  }
                  parentNode.style.transition = 'all 0.3s';
                  parentNode.style.transform = isChecked ? 'translateX(-100px)' : 'translateX(0px)';
                  parentNode.style.height = isChecked ? '0px' : 'auto';
                  parentNode.style.padding = isChecked ? '0px' : '32px';
                  parentNode.style.opacity = isChecked ? '0' : '1';

                  // setTimeout(() => {
                  //   if (isChecked) {
                  //     parentNode.style.display = 'none';
                  //     parentNode.style.transform = 'translateX(0px)';
                  //   }
                  // }, 300);

                  const newSelectedZones = [...selectedZones, zone.uuid];
                  setTimeout(() => {
                    setSelectedZones(newSelectedZones);
                  }, 300);
                }} />
              </Box>
            </Box>
          ))}
        </Box>
        {/* )} */}
      </Box>
    </Stack>
  )
}